import {
    useAppDisplayModeContext,
    useStyles,
    pxArrayToRem,
    Container,
    Divider,
    useTheme,
} from '@archipro-design/aria';

import { Link } from '@remix-run/react';
import { TileGrid } from '@modules/category/component/tile-grid/TileGrid';
import { NoSearchResults } from '@modules/professional/component/shared/NoSearchResults';

import * as S from '@modules/professional/component/shared/PaginatedStyles.style';

import { useSaveToDesignBoard } from '~/modules/design-board/hook/use-save-to-design-board';
import SaveToDesignBoard from '@modules/design-board/component/SaveToDesignBoard';
import { SearchFilter } from '@modules/professional/component/shared/SearchFilter';
import { useState } from 'react';
import FilterDialogMobile from '@modules/professional/component/shared/FilterDialog.mobile';
import { useTracker } from '@archipro-website/tracker';
import type { DefaultElasticSearchResults } from '~/modules/professional/type';

import { useToggle } from 'ahooks';
import { CollapsiblePageLayout } from '~/modules/category/component/collapsible-page-layout/CollapsiblePageLayout';
import { useSearchFilters } from '@modules/search/hook/use-search-filters';
import { useAppData, useMatchesHandleData } from '~/modules/root/hook';
import type { ElasticSearchCategories } from '~/modules/search/type';

import { IN_PAGE_SEARCH_IGNORE_FILTER_KEYS } from '~/modules/professional/config/constants';
import { CategoryFilterNav } from '@modules/category/component/category-filter-section/CategoryFilterNav';
import AppliedFilters from '~/modules/search/components/search-filter-section/AppliedFilters';
import { resetFilterStates } from '~/modules/search/util/reset-filter-states';
import { Container as PageLayoutContainer } from '@modules/professional/component/page-layout/PageLayout.style';
import ProjectTile from '~/modules/tile/component/project-tile/ProjectImpressionTile';
import { visitBMEditor } from '~/modules/directory/util/tile-helpers';
import DeleteItemConfirmModal from '~/modules/directory/component/DeleteItemConfirmModal';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import { useDeleteItemWithConfirm } from '~/modules/directory/hook/use-delete-item-with-confirm';
import type { SyntheticEvent } from 'react';
import { useTileHotButtons } from '~/modules/root/hook';
import {
    PROJECT_TILE_BULLET_SIZE,
    PROJECT_TILE_COLLAPSED_WIDTH,
    PROJECT_TILE_EXPANDED_WIDTH,
    PROJECT_TILE_MIN_SAVED_COUNT_THRESHOLD,
    PROJECT_TILE_MIN_VIEW_COUNT_THRESHOLD,
    PROJECT_TILE_PAGING_WIDTH,
} from '~/routes/_app.projects.$';
import AwardIndicator from '@modules/page-builder/component/detail/banner-awards/AwardIndicator';
import { hasFeature } from '@modules/root';
import { useABTestIsOn } from '@modules/root/hook/use-growthbook';
import type { ProjectTileViewModel } from '@modules/search/util/elasticsearch-project-tile-view';

interface PaginatedProjectsProp extends DefaultElasticSearchResults {
    projectTiles: ProjectTileViewModel[];
    disableTileLink?: boolean;
    disableSaveToDesignBoard?: boolean;
    disableSort?: boolean;
}

export const PaginatedProjects: React.FC<PaginatedProjectsProp> = ({
    pagination,
    projectTiles,
    searchResult,
    filterStates,
    disableTileLink,
    disableSaveToDesignBoard,
    disableSort,
}) => {
    const hasPagination = pagination.totalPages !== 1;
    const { css } = useStyles();
    const { siteVariables } = useTheme();
    const appDisplayMode = useAppDisplayModeContext();
    const { desktop } = appDisplayMode;
    const tracker = useTracker();
    const isBMPreview = useMatchesHandleData('isBMPreview', false);

    const { user } = useAppData();
    const enableAwardBadges = hasFeature(user.FeatureFlags, 'award_badges');

    const [filterDrawerOpen, { toggle: toggleFilterDrawer }] =
        useToggle(!disableSort);
    const gridColumns = desktop ? 3 : 1;
    const gridColumnWidth = filterDrawerOpen
        ? PROJECT_TILE_COLLAPSED_WIDTH
        : PROJECT_TILE_EXPANDED_WIDTH;
    const gridColumnPagingSpacer =
        (gridColumnWidth - PROJECT_TILE_PAGING_WIDTH) / 2;

    const { favouriteItem } = useSaveToDesignBoard('ProjectPin');

    const {
        itemID: itemIDToDelete,
        willDelete,
        confirmDelete,
        cancelDelete,
    } = useDeleteItemWithConfirm();

    const projectIDs = projectTiles.map((project) => project.tile.projectID);
    const renderTileAs = disableTileLink ? 'div' : Link;

    const { getTileTopActions, hotButtonModal, getTileAdminCaption } =
        useTileHotButtons({
            itemType: 'project',
            itemIDs: projectIDs,
        });

    const showSlider = useABTestIsOn('directory-tile-slider');
    const isStaffUser = user.__typename === 'Me' && user.IsArchiproStaff;
    const showViewCount = useABTestIsOn('project_tile_stats') && isStaffUser;

    const tileLen = projectTiles.length;
    const isProfessionalProfile = useMatchesHandleData(
        'isProfessionalProfile',
        false
    );
    const tiles = projectTiles.map((project, index) => {
        const { tile } = project;
        const tilePaddingBottom =
            desktop &&
            !hasPagination &&
            tileLen - (tileLen % gridColumns) <= index &&
            tileLen - index <= gridColumns
                ? 0
                : 22;

        const images = showSlider ? project.sliderImages : tile.image;
        const imageProps = images.map((image) => {
            return {
                src: image,
                width: '100%',
                height: '100%',
                sizes: getImagesSizes(585),
                alt: tile.projectName,
            };
        });

        const editOnSameTab = disableTileLink || isBMPreview;

        const topActions = getTileTopActions({
            item: tile,
            editOnSameTab,
            onDeleteClick: () => willDelete(tile.projectID),
        });
        const adminCaption = getTileAdminCaption(tile.projectID);
        const linkProps = !disableTileLink
            ? {
                  to: tile.projectLink,
                  prefetch: 'intent',
                  target: !isProfessionalProfile ? '_parent' : undefined,
                  state: {
                      visitFrom: isProfessionalProfile ? 'profile' : undefined,
                  },
              }
            : {};

        const paddleIndent =
            imageProps.length < 5
                ? (5 - imageProps.length) * PROJECT_TILE_BULLET_SIZE
                : 0;
        const paginationPadding = gridColumnPagingSpacer + paddleIndent;

        const displayViewCounts =
            showViewCount &&
            tile.viewedCount &&
            tile.viewedCount >= PROJECT_TILE_MIN_VIEW_COUNT_THRESHOLD;
        const displaySavedCounts =
            showViewCount &&
            tile.savedCount &&
            tile.savedCount >= PROJECT_TILE_MIN_SAVED_COUNT_THRESHOLD;

        return (
            <ProjectTile
                key={tile.projectLink}
                as={renderTileAs}
                {...linkProps}
                image={imageProps}
                projectName={{
                    children: tile.projectName,
                    weight: 'regular',
                }}
                professionalName={{
                    children: tile.professionalName,
                }}
                variant={desktop ? '01' : '02'}
                size={
                    !desktop
                        ? 'dynamic'
                        : !filterDrawerOpen
                          ? 'large'
                          : 'medium'
                }
                hideFavourite={disableSaveToDesignBoard}
                onFavourite={(e) => {
                    favouriteItem.onClick(e, tile.projectID);
                }}
                isFavourited={favouriteItem.favourited(tile.projectID)}
                onClick={(e: SyntheticEvent) => {
                    if (isBMPreview) {
                        // prevent default to avoid navigating to public page, onTileClick should handle the navigation in BM
                        e.preventDefault();
                    }
                    tracker.log('ProfessionalProjectClick', {
                        url: new URL(window.location.href),
                        targetTracker: 'archiproTracker',
                    });

                    if (
                        (isBMPreview || disableTileLink) &&
                        Boolean(tile.canEdit)
                    ) {
                        visitBMEditor(tile, true);
                    }
                }}
                variables={{
                    tilePadding: pxArrayToRem([14, 14, tilePaddingBottom, 14]),
                    ...(!desktop && {
                        dynamicSizingWidth: 414,
                        dynamicSizingGapWidth: 0,
                        tilePadding: 0,
                    }),
                    paginationPadding,
                }}
                impression={{
                    type: 'LibraryItem_Directory',
                    itemID: tile.projectID,
                    professionalID: tile.professionalID,
                }}
                sectionSource={'Profile'}
                topActions={topActions}
                adminCaption={adminCaption}
                newDesignEnabled={true}
                awardIndicator={
                    enableAwardBadges &&
                    tile.hasAwards && <AwardIndicator size={'sm'} />
                }
                viewedCount={displayViewCounts ? tile.viewedCount : undefined}
                savedCount={displaySavedCounts ? tile.savedCount : undefined}
            />
        );
    });

    const [openFilterDialog, setOpenFilterDialog] = useState(false);

    const totalCount = searchResult?.nbHits ?? 0;

    const navFilterButton = {
        children: `Filters`,
        transparent: true,
        onClick: () => setOpenFilterDialog(true),
    };

    const ignoreFilterKeys = IN_PAGE_SEARCH_IGNORE_FILTER_KEYS;
    const category = useMatchesHandleData<ElasticSearchCategories>(
        'category',
        'project'
    );

    const searchFilters = useSearchFilters({
        category,
        initialStates: filterStates,
        isCategoryPage: false,
        ignoreFilterKeys,
    });

    return (
        <Container>
            {desktop && (
                <div className={css(PageLayoutContainer)}>
                    <Divider
                        size={2}
                        variables={{
                            dividerPadding: 0,
                            dividerPrimaryColor: siteVariables.colors.grey[125],
                        }}
                    />
                </div>
            )}
            <div className={css(S.Wrap)}>
                <CollapsiblePageLayout
                    sidePanel={
                        desktop ? (
                            <div className={css(S.FilterSection)}>
                                <CategoryFilterNav
                                    category="project"
                                    searchFilters={searchFilters}
                                />
                            </div>
                        ) : null
                    }
                    hideSidePanel={!filterDrawerOpen}
                    content={
                        <>
                            <div className={css(S.FilterTogglesSection)}>
                                <SearchFilter
                                    searchFilters={searchFilters}
                                    filterButton={navFilterButton}
                                    initialStates={filterStates}
                                    filterDrawerOpen={filterDrawerOpen}
                                    toggleFilterDrawer={toggleFilterDrawer}
                                    disableSort={disableSort}
                                />
                            </div>
                            {searchFilters.appliedFilters &&
                                searchFilters.appliedFilters.length > 0 && (
                                    <AppliedFilters
                                        filterItems={
                                            searchFilters.appliedFilters
                                        }
                                        resetFilters={() =>
                                            searchFilters.resetFilters(
                                                resetFilterStates(
                                                    searchFilters.filterStates,
                                                    [
                                                        ...ignoreFilterKeys,
                                                        'category',
                                                    ]
                                                )
                                            )
                                        }
                                    />
                                )}
                            <div className={css(S.ResultSection)}>
                                {!tiles.length ? (
                                    <NoSearchResults resultsType={'projects'} />
                                ) : (
                                    <TileGrid
                                        items={tiles}
                                        columns={gridColumns}
                                        columnWidth={gridColumnWidth}
                                        pagination={pagination}
                                        paginationItem={'Projects'}
                                        onPageChange={(page: number) => {
                                            tracker.log(
                                                'ProfessionalProjectPageChange',
                                                {
                                                    url: new URL(
                                                        window.location.href
                                                    ),
                                                    targetTracker:
                                                        'archiproTracker',
                                                    data: {
                                                        ExtraData: page,
                                                    },
                                                }
                                            );
                                        }}
                                        type="projects"
                                    />
                                )}
                            </div>
                        </>
                    }
                />
                {!disableSaveToDesignBoard && favouriteItem.target && (
                    <SaveToDesignBoard {...favouriteItem} />
                )}
                {itemIDToDelete && (
                    <DeleteItemConfirmModal
                        title={'Delete project'}
                        content={
                            'Are you sure you would like to delete this project? All information and associated tags will be lost.'
                        }
                        open={true}
                        onCancel={cancelDelete}
                        onConfirm={confirmDelete}
                    />
                )}
                <FilterDialogMobile
                    openFilterDialog={openFilterDialog}
                    itemCount={totalCount}
                    onClose={() => setOpenFilterDialog(false)}
                    filterComponent={<div>project-filters goes here</div>}
                />
                {hotButtonModal}
            </div>
        </Container>
    );
};
